import './bitrix-documents.scss';

import { Modal, Pagination, Switch } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useParams } from 'react-router';

import { base64toBlob } from '@/components/App/Approvement/helpers';
import { AllurLoader, Button } from '@/components/Shared/ui';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';
import FileTextIcon from '@/media/icons/file-text.svg?react';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  setIsBitrixDocumentsAccepted,
  setIsEuBankOfflineProcess
} from '@/store/financingStep.slice';
import type { IBitrixDocuments } from '@/types/financing';

import { BITRIDX_DOCUMENTS_NAMES } from './constants';

// setup worker for react pdf
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

type IDocumentsState = Omit<
  IBitrixDocuments,
  'dkpNumber' | 'dkpSignDate'
> | null;
type IDkpState = Pick<IBitrixDocuments, 'dkpNumber' | 'dkpSignDate'> | null;

const BitrixDocuments: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const dispatch = useAppDispatch();
  const { isBitrixDocumentsAccepted, isEuBankOfflineProcess } = useAppSelector(
    (state) => state.financingStep
  );

  const [documents, setDocuments] = useState<IDocumentsState>(null);
  const [dkpInfo, setDkpInfo] = useState<IDkpState>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pagesCount, setPagesCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);

  const [shownDocument, setShownDocument] = useState<string | null>(null);

  const pdfObjectUrl = useMemo(() => {
    if (!shownDocument) return null;
    return URL.createObjectURL(
      base64toBlob(
        `data:application/pdf;base64,${shownDocument}`,
        'application/pdf'
      )
    );
  }, [shownDocument]);

  const changeBitrixDocumentsAccepted = () => {
    dispatch(setIsBitrixDocumentsAccepted(!isBitrixDocumentsAccepted));
    dispatch(setIsEuBankOfflineProcess(false));
  };

  const onChangePage = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPagesCount(numPages);
  };

  useEffect(() => {
    (async () => {
      if (uuid) {
        setIsLoading(true);
        try {
          const response = await autoCreditApi.getBitrixDocuments(uuid);
          setDocuments({
            customs_clearance_document: response.customs_clearance_document,
            dkp: response.dkp,
            obligations_confirmation_document:
              response.obligations_confirmation_document,
            util_payment: response.util_payment
          });
          setDkpInfo({
            dkpNumber: response.dkpNumber,
            dkpSignDate: response.dkpSignDate
          });
        } catch (error) {
          handleError(error, {
            notify: false,
            onError: (e) => {
              setErrorMessage(e.message);
            }
          });
        }
        setIsLoading(false);
      }
    })();
  }, [uuid]);

  useEffect(() => {
    if (isBitrixDocumentsAccepted) {
      dispatch(setIsBitrixDocumentsAccepted(false));
    }
  }, []);

  return (
    <div
      className={`bitrix-documents ${isEuBankOfflineProcess ? 'bitrix-documents--offline-process' : ''}`}
    >
      <div className="bitrix-documents__content">
        <h2 className="bitrix-documents__title">Документы от битрикса</h2>

        {isLoading ? (
          <AllurLoader fullWidth />
        ) : errorMessage ? (
          <div className="bitrix-documents__error">
            <AlertCircleIcon />
            {errorMessage}
          </div>
        ) : (
          <>
            <div className="bitrix-documents__list">
              {Object.entries(documents || {}).map(([key, value]) => (
                <div
                  key={key}
                  className="bitrix-documents__item"
                  onClick={() => setShownDocument(value)}
                >
                  <div className="bitrix-documents__item-info">
                    <FileTextIcon />
                    <span className="bitrix-documents__label">
                      {BITRIDX_DOCUMENTS_NAMES[
                        key as keyof typeof BITRIDX_DOCUMENTS_NAMES
                      ] || key}
                    </span>
                  </div>
                  {key === 'dkp' && (
                    <div className="bitrix-documents__dkp-info">
                      <p className="bitrix-documents__dkp-info-text">
                        <span className="bitrix-documents__dkp-label">
                          Номер ДКП:{' '}
                        </span>
                        <b>{dkpInfo?.dkpNumber || 'Не указан'}</b>
                      </p>
                      <p className="bitrix-documents__dkp-info-text">
                        <span className="bitrix-documents__dkp-label">
                          Дата подписания ДКП:{' '}
                        </span>
                        <b>{dkpInfo?.dkpSignDate || 'Не указана'}</b>
                      </p>
                    </div>
                  )}
                </div>
              ))}

              <label className="bitrix-documents__switch">
                <Switch
                  onChange={changeBitrixDocumentsAccepted}
                  checked={isBitrixDocumentsAccepted}
                />
                Документы проверены
              </label>
            </div>

            <Modal
              open={!!shownDocument}
              onCancel={() => setShownDocument(null)}
              footer={null}
              width={800}
              centered
              className="bitrix-documents-modal"
              closeIcon={null}
            >
              <Document
                file={pdfObjectUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                className="bitrix-documents-modal__document"
                loading={<AllurLoader fullWidth />}
              >
                <Page
                  className="bitrix-documents-modal__page"
                  pageNumber={pageNumber}
                  width={720}
                />
              </Document>
              {pagesCount > 1 && (
                <Pagination
                  className="bitrix-documents-modal__pagination"
                  defaultCurrent={1}
                  total={pagesCount * 10}
                  onChange={onChangePage}
                />
              )}
              <Button
                className="bitrix-documents-modal__close"
                onClick={() => setShownDocument(null)}
                variant="filled-red"
              >
                Закрыть
              </Button>
            </Modal>
          </>
        )}
      </div>

      <label className="bitrix-documents__offline-switch">
        <Switch
          checked={isEuBankOfflineProcess}
          onChange={(checked) => dispatch(setIsEuBankOfflineProcess(checked))}
          disabled={isBitrixDocumentsAccepted}
        />
        Продолжить заявку по оффлайн процессу
      </label>
    </div>
  );
};

export default BitrixDocuments;
